/**
 * Deterministic MessagePack encoder/decoder for BUD-16 / BUD-17 tree nodes.
 *
 * BUD-16 fixes the encoding so two implementations that describe the same tree
 * produce the same bytes, and therefore the same Blossom hash:
 *
 *   1. Root map fields in the order `l`, `t`.
 *   2. Link map fields in the order `h`, `k`, `m`, `n`, `s`, `t` (absent optional fields omitted).
 *   3. Directory links sorted by `n`, ascending bytewise over the UTF-8 bytes.
 *   4. Metadata keys sorted ascending bytewise over the UTF-8 bytes.
 *   5. Shortest integer encoding that can represent the value.
 *   6. Binary values use MessagePack `bin` types, never strings.
 *
 * We hand-roll this instead of pulling in a msgpack library because the subset
 * a tree node needs is tiny, and because the explainer shows the bytes.
 */

/** A node's `t` value. Link `t` values reuse these, plus `0` for a raw blob. */
export const NodeType = {
  /** BUD-17 file manifest. */
  File: 1,
  /** BUD-16 directory manifest. */
  Directory: 2,
  /** BUD-17 directory fanout node. */
  Fanout: 3,
} as const;

/** A link `t` value: a raw Blossom blob with no node type of its own. */
export const LinkTypeBlob = 0;

export type LinkType = 0 | 1 | 2 | 3;
export type NodeTypeValue = 1 | 2 | 3;

/** JSON-compatible metadata. BUD-16 requires string keys. */
export type Metadata = Record<string, string | number | boolean>;

export interface Link {
  /** 32 byte Blossom hash of the linked object. */
  h: Uint8Array;
  /** 32 byte client-side decryption key. A bearer secret — never sent to servers. */
  k?: Uint8Array;
  /** JSON-compatible metadata. */
  m?: Metadata;
  /** Directory entry name. Required in `t = 2` nodes, forbidden in `t = 1` and `t = 3`. */
  n?: string;
  /** Plaintext size this link represents. */
  s: number;
  /** Type of the linked object. */
  t: LinkType;
}

export interface TreeNode {
  l: Link[];
  t: NodeTypeValue;
}

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", { fatal: true });

class ByteWriter {
  private parts: number[] = [];

  push(...bytes: number[]): void {
    for (const byte of bytes) this.parts.push(byte);
  }

  pushBytes(bytes: Uint8Array): void {
    for (const byte of bytes) this.parts.push(byte);
  }

  toBytes(): Uint8Array {
    return new Uint8Array(this.parts);
  }
}

/** Shortest MessagePack encoding for a non-negative integer. */
function writeUint(out: ByteWriter, value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`cannot encode ${value} as a non-negative integer`);
  }
  if (value < 0x80) {
    out.push(value); // positive fixint
  } else if (value <= 0xff) {
    out.push(0xcc, value); // uint 8
  } else if (value <= 0xffff) {
    out.push(0xcd, value >>> 8, value & 0xff); // uint 16
  } else if (value <= 0xffffffff) {
    out.push(0xce, (value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff); // uint 32
  } else {
    // uint 64. Split around 2^32 because bitwise ops in JS are 32 bit.
    const high = Math.floor(value / 0x100000000);
    const low = value >>> 0;
    out.push(
      0xcf,
      (high >>> 24) & 0xff,
      (high >>> 16) & 0xff,
      (high >>> 8) & 0xff,
      high & 0xff,
      (low >>> 24) & 0xff,
      (low >>> 16) & 0xff,
      (low >>> 8) & 0xff,
      low & 0xff,
    );
  }
}

function writeString(out: ByteWriter, value: string): void {
  const bytes = textEncoder.encode(value);
  if (bytes.length < 32) {
    out.push(0xa0 | bytes.length); // fixstr
  } else if (bytes.length <= 0xff) {
    out.push(0xd9, bytes.length); // str 8
  } else if (bytes.length <= 0xffff) {
    out.push(0xda, bytes.length >>> 8, bytes.length & 0xff); // str 16
  } else {
    out.push(0xdb, (bytes.length >>> 24) & 0xff, (bytes.length >>> 16) & 0xff, (bytes.length >>> 8) & 0xff, bytes.length & 0xff);
  }
  out.pushBytes(bytes);
}

function writeBinary(out: ByteWriter, value: Uint8Array): void {
  if (value.length <= 0xff) {
    out.push(0xc4, value.length); // bin 8
  } else if (value.length <= 0xffff) {
    out.push(0xc5, value.length >>> 8, value.length & 0xff); // bin 16
  } else {
    out.push(0xc6, (value.length >>> 24) & 0xff, (value.length >>> 16) & 0xff, (value.length >>> 8) & 0xff, value.length & 0xff);
  }
  out.pushBytes(value);
}

function writeMapHeader(out: ByteWriter, size: number): void {
  if (size < 16) {
    out.push(0x80 | size); // fixmap
  } else if (size <= 0xffff) {
    out.push(0xde, size >>> 8, size & 0xff); // map 16
  } else {
    throw new Error("map too large");
  }
}

function writeArrayHeader(out: ByteWriter, size: number): void {
  if (size < 16) {
    out.push(0x90 | size); // fixarray
  } else if (size <= 0xffff) {
    out.push(0xdc, size >>> 8, size & 0xff); // array 16
  } else {
    throw new Error("array too large");
  }
}

/** Ascending bytewise comparison of two strings' UTF-8 encodings. */
export function compareUtf8(a: string, b: string): number {
  const left = textEncoder.encode(a);
  const right = textEncoder.encode(b);
  const shared = Math.min(left.length, right.length);
  for (let i = 0; i < shared; i++) {
    if (left[i]! !== right[i]!) return left[i]! - right[i]!;
  }
  return left.length - right.length;
}

function writeMetadata(out: ByteWriter, metadata: Metadata): void {
  // Rule 4: metadata keys in ascending bytewise order.
  const keys = Object.keys(metadata).sort(compareUtf8);
  writeMapHeader(out, keys.length);
  for (const key of keys) {
    writeString(out, key);
    const value = metadata[key]!;
    if (typeof value === "string") writeString(out, value);
    else if (typeof value === "number") writeUint(out, value);
    else out.push(value ? 0xc3 : 0xc2);
  }
}

function writeLink(out: ByteWriter, link: Link): void {
  // Rule 2: `h`, `k`, `m`, `n`, `s`, `t`, omitting absent optional fields.
  // `h`, `s` and `t` are always present; `k`, `m` and `n` are optional.
  const fields = 3 + (link.k ? 1 : 0) + (link.m ? 1 : 0) + (link.n !== undefined ? 1 : 0);
  writeMapHeader(out, fields);

  writeString(out, "h");
  writeBinary(out, link.h);

  if (link.k) {
    writeString(out, "k");
    writeBinary(out, link.k);
  }
  if (link.m) {
    writeString(out, "m");
    writeMetadata(out, link.m);
  }
  if (link.n !== undefined) {
    writeString(out, "n");
    writeString(out, link.n);
  }

  writeString(out, "s");
  writeUint(out, link.s);

  writeString(out, "t");
  writeUint(out, link.t);
}

/**
 * Encode a tree node to its canonical MessagePack bytes.
 *
 * Links are written in the order given. Callers building a `t = 2` directory
 * must sort by name first — `sortDirectoryLinks` does that.
 */
export function encodeNode(node: TreeNode): Uint8Array {
  const out = new ByteWriter();

  // Rule 1: root fields in the order `l`, `t`.
  writeMapHeader(out, 2);
  writeString(out, "l");
  writeArrayHeader(out, node.l.length);
  for (const link of node.l) writeLink(out, link);
  writeString(out, "t");
  writeUint(out, node.t);

  return out.toBytes();
}

/** Rule 3: directory links sorted by `n`, ascending bytewise over UTF-8 bytes. */
export function sortDirectoryLinks(links: Link[]): Link[] {
  return [...links].sort((a, b) => compareUtf8(a.n ?? "", b.n ?? ""));
}

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

class ByteReader {
  offset = 0;
  constructor(readonly bytes: Uint8Array) {}

  private take(count: number): Uint8Array {
    if (this.offset + count > this.bytes.length) throw new Error("truncated MessagePack input");
    const slice = this.bytes.subarray(this.offset, this.offset + count);
    this.offset += count;
    return slice;
  }

  u8(): number {
    if (this.offset >= this.bytes.length) throw new Error("truncated MessagePack input");
    return this.bytes[this.offset++]!;
  }

  uint(count: number): number {
    let value = 0;
    for (const byte of this.take(count)) value = value * 256 + byte;
    return value;
  }

  bytesOf(count: number): Uint8Array {
    return new Uint8Array(this.take(count));
  }
}

export type DecodedValue = null | boolean | number | string | Uint8Array | DecodedValue[] | { [key: string]: DecodedValue };

function decodeValue(reader: ByteReader): DecodedValue {
  const prefix = reader.u8();

  if (prefix <= 0x7f) return prefix; // positive fixint
  if (prefix >= 0xe0) return prefix - 256; // negative fixint
  if ((prefix & 0xf0) === 0x80) return decodeMap(reader, prefix & 0x0f); // fixmap
  if ((prefix & 0xf0) === 0x90) return decodeArray(reader, prefix & 0x0f); // fixarray
  if ((prefix & 0xe0) === 0xa0) return textDecoder.decode(reader.bytesOf(prefix & 0x1f)); // fixstr

  switch (prefix) {
    case 0xc0: return null;
    case 0xc2: return false;
    case 0xc3: return true;
    case 0xc4: return reader.bytesOf(reader.uint(1)); // bin 8
    case 0xc5: return reader.bytesOf(reader.uint(2)); // bin 16
    case 0xc6: return reader.bytesOf(reader.uint(4)); // bin 32
    case 0xcc: return reader.uint(1); // uint 8
    case 0xcd: return reader.uint(2); // uint 16
    case 0xce: return reader.uint(4); // uint 32
    case 0xcf: return reader.uint(8); // uint 64
    case 0xd9: return textDecoder.decode(reader.bytesOf(reader.uint(1))); // str 8
    case 0xda: return textDecoder.decode(reader.bytesOf(reader.uint(2))); // str 16
    case 0xdb: return textDecoder.decode(reader.bytesOf(reader.uint(4))); // str 32
    case 0xdc: return decodeArray(reader, reader.uint(2)); // array 16
    case 0xdd: return decodeArray(reader, reader.uint(4)); // array 32
    case 0xde: return decodeMap(reader, reader.uint(2)); // map 16
    case 0xdf: return decodeMap(reader, reader.uint(4)); // map 32
    default: throw new Error(`unsupported MessagePack prefix 0x${prefix.toString(16)}`);
  }
}

function decodeArray(reader: ByteReader, size: number): DecodedValue[] {
  const items: DecodedValue[] = [];
  for (let i = 0; i < size; i++) items.push(decodeValue(reader));
  return items;
}

function decodeMap(reader: ByteReader, size: number): { [key: string]: DecodedValue } {
  const map: { [key: string]: DecodedValue } = {};
  for (let i = 0; i < size; i++) {
    const key = decodeValue(reader);
    if (typeof key !== "string") throw new Error("MessagePack map keys must be strings");
    map[key] = decodeValue(reader);
  }
  return map;
}

/** Decode arbitrary MessagePack. Used by the inspector to show what a manifest really contains. */
export function decode(bytes: Uint8Array): DecodedValue {
  const reader = new ByteReader(bytes);
  const value = decodeValue(reader);
  if (reader.offset !== bytes.length) throw new Error("trailing bytes after MessagePack value");
  return value;
}

/** Decode and validate a tree node, rejecting unknown node and link types per BUD-16. */
export function decodeNode(bytes: Uint8Array): TreeNode {
  const value = decode(bytes);
  if (value === null || typeof value !== "object" || Array.isArray(value) || value instanceof Uint8Array) {
    throw new Error("tree node must be a MessagePack map");
  }

  const nodeType = value.t;
  if (nodeType !== 1 && nodeType !== 2 && nodeType !== 3) {
    // BUD-16: readers MUST return an unsupported type error rather than guessing.
    throw new Error(`unsupported node type ${String(nodeType)}`);
  }

  const rawLinks = value.l;
  if (!Array.isArray(rawLinks)) throw new Error("tree node `l` must be an array");

  const links: Link[] = rawLinks.map((raw, index) => {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw) || raw instanceof Uint8Array) {
      throw new Error(`link ${index} must be a map`);
    }
    const { h, k, m, n, s, t } = raw;
    if (!(h instanceof Uint8Array) || h.length !== 32) throw new Error(`link ${index} has an invalid \`h\``);
    if (k !== undefined && (!(k instanceof Uint8Array) || k.length !== 32)) throw new Error(`link ${index} has an invalid \`k\``);
    if (typeof s !== "number") throw new Error(`link ${index} has an invalid \`s\``);
    if (t !== 0 && t !== 1 && t !== 2 && t !== 3) throw new Error(`link ${index} has unsupported type ${String(t)}`);
    if (n !== undefined && typeof n !== "string") throw new Error(`link ${index} has an invalid \`n\``);

    return {
      h,
      ...(k !== undefined ? { k } : {}),
      ...(m !== undefined ? { m: m as Metadata } : {}),
      ...(n !== undefined ? { n } : {}),
      s,
      t,
    };
  });

  return { l: links, t: nodeType };
}
